import { ArrowLeft } from 'lucide-react';
import { useRef, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { useScrollMemory } from '@/hooks/useScrollMemory';

interface ScreenProps {
  title: string;
  description?: string;
  actions?: ReactNode;
  /**
   * 戻り先パス。指定するとタイトル左にアイコンのみの戻るボタンを描画。
   * 編集系画面の「一覧へ戻る」を統一スタイルで提供する。
   */
  backTo?: string;
  /**
   * スクロール位置を覚えて、戻る (ブラウザの戻る / 商談詳細の「前の画面へ」) で開き直した時に戻す。
   * 詳細画面への入口になる一覧・ダッシュボードで指定する (2026-10-30)。
   */
  restoreScroll?: boolean;
  children: ReactNode;
}

/** スクリーン全体構造。ヘッダー（タイトル + アクション）+ スクロール可能ボディ。 */
export function Screen({
  title,
  description,
  actions,
  backTo,
  restoreScroll = false,
  children,
}: ScreenProps) {
  const navigate = useNavigate();
  const bodyRef = useRef<HTMLDivElement>(null);
  useScrollMemory(bodyRef, restoreScroll);
  return (
    <div className="screen">
      <header className="screen-header flex items-start justify-between gap-4">
        <div className="flex items-center gap-2">
          {backTo && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => navigate(backTo)}
              aria-label="一覧へ戻る"
            >
              <ArrowLeft className="size-4" />
            </Button>
          )}
          <div>
            <h2>{title}</h2>
            {description && <p>{description}</p>}
          </div>
        </div>
        {actions && <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>}
      </header>
      <div ref={bodyRef} className="screen-body fade-in">
        {children}
      </div>
    </div>
  );
}
